import {
  localPaymentMethodMap,
  PaymentIcon,
  PaymentType,
  SubPaymentType,
} from '@thg-commerce/enterprise-config'
import { Product } from '@thg-commerce/enterprise-pages/src/Basket/SelectYourSample/SelectYourSampleContext'
import { ProductPriceProps } from '@thg-commerce/gravity-elements'
import { BasketIconProps } from '@thg-commerce/gravity-patterns/Header/BasketIcon'

import { ColourSwatch } from '../../types/enterpriseTypes'
import { DescriptionKeys, productDescription } from './product/description'
import {
  AppliedOffersFragment,
  AvailablePaymentOption,
  BasketItemMessageType,
  BasketItemsFragment,
  BasketMessageType,
  FulfilmentMethod,
  MoneyValue,
  RichContent,
  SelectYourSampleFragment,
  SharedBasketFragment,
} from '../generated/graphql'

export type NormalisedRecommendation = {
  sku: number
  title: string
  price?: ProductPriceProps
  url: string
  image: string
  reviews: {
    total: number
    averageScore: number
    __typename: 'ProductReviews'
  }
  options: {
    key: string
    choices: {
      colour?: string
      key: string
      title: string
    }[]
  }[]
  variants: {
    choices: {
      key: string
      optionKey: string
    }[]
    inStock: boolean
    product?: {
      url?: string
    } | null
  }[]
  outOfGauge?: boolean
  eligibleForHomeDelivery?: boolean
  eligibleForDatedDelivery?: boolean
  eligibleForNextDayDelivery?: boolean
  storeDeliveryEnabled?: boolean
  pricePerUnit: {
    displayValue?: string
    unit?: string
  }
  colourSwatches: ColourSwatch
  __typename: 'Product'
}

type ProductContentRichContentListValue = {
  key: string
  value: {
    richContentListValue: (Omit<RichContent, '__typename'> & {
      __typename: 'RichContent'
    })[]
    __typename: 'ProductContentRichContentListValue'
  }
  __typename: 'ProductContentItem'
}

type ProductContentRichContentValue = {
  key: string
  value: {
    richContentValue: Omit<RichContent, '__typename'> & {
      __typename: 'RichContent'
    }
    __typename: 'ProductContentRichContentValue'
  }
}

type ProductContentStringListValue = {
  key: string
  value: {
    stringListValue: string[]
    __typename: 'ProductContentStringListValue'
  }
}

type ProductContentStringValue = {
  key: string
  value: {
    stringValue: string
    __typename: 'ProductContentStringValue'
  }
}

type QubitBasketItem = {
  __typename?: 'QubitBasketItem'
  product?: {
    productId: string
    sku: string
    name: string
    price: {
      value: number
      currency: string
    }
  }
  basket: {
    total: {
      value: number
    }
    quantity: number
  }
  subtotal: {
    value: number
  }
  subtotalIncludingTax: {
    value: number
  }
  quantity: number
}

type SysTier = SharedBasketFragment['selectYourSample'][0]['tiers'][0] & {
  sysProducts: {
    productVariants: SharedBasketFragment['selectYourSample'][0]['tiers'][0]['products']
    products: {
      sku: String
      variants: SharedBasketFragment['selectYourSample'][0]['tiers'][0]['products']
    }
  }
  selectedSysProducts: {
    productVariants: SharedBasketFragment['selectYourSample'][0]['tiers'][0]['selectedProducts']
    products: {
      sku: String
      variants: SharedBasketFragment['selectYourSample'][0]['tiers'][0]['selectedProducts']
    }
  }
}

export type NormalisedBasket = Omit<
  SharedBasketFragment,
  | 'availablePaymentOptions'
  | 'appliedOffers'
  | 'items'
  | 'recommendations'
  | 'selectYourSample'
> & {
  appliedOffers: AppliedOffersFragment['appliedOffers'] &
    {
      amount?: string | null
      displayValue?: string | null
    }[]
  discount: {
    displayValue: string
    amount: string
    __typename?: 'MoneyValue'
  }
  availablePaymentOptions: {
    icon: PaymentIcon
    type: PaymentType
    subType: SubPaymentType | null
    basketOrder?: number
    __typename: 'AvailablePaymentOption'
  }[]
  items: BasketItemsFragment['items']
  totalQuantity: BasketIconProps['totalQuantity']
  selectYourSample: (Omit<
    SelectYourSampleFragment['selectYourSample'][0],
    'tiers'
  > & {
    tiers: (Omit<
      SelectYourSampleFragment['selectYourSample'][0]['tiers'][0],
      'additionalSpendToReach' | 'products'
    > & {
      products: Product[]
      additionalSpendToReach: Omit<MoneyValue, 'currency' | 'scalarValue'>
      isFullyOutOfStock: boolean
      isPartiallyOutOfStock: boolean
      id: string
    })[]
  })[]
  recommendations: NormalisedRecommendation[]
  hasProductWithClickAndCollectFulfilment: boolean
  qubitBasketItems: QubitBasketItem[] | null
}

const filterAndSortSelectYourSampleProducts = (
  productVariants: SharedBasketFragment['selectYourSample'][0]['tiers'][0]['products'],
  productDescriptionKeys?: DescriptionKeys,
): Product[] => {
  return productVariants
    .map((productVariant) => ({
      ...productVariant,
      content: productVariant.content.filter(
        (
          contentItem,
        ): contentItem is
          | ProductContentRichContentListValue
          | ProductContentRichContentValue
          | ProductContentStringListValue
          | ProductContentStringValue => contentItem !== undefined,
      ),
      descriptionContent: productDescriptionKeys
        ? productDescription(
            productVariant.product as Product,
            productDescriptionKeys,
          )
        : null,
    }))
    .sort((a, b) => (a.title < b.title ? -1 : 1))
}

const transformPaymentOptions = (paymentOptions: AvailablePaymentOption[]) => {
  const formattedPaymentOptions = paymentOptions.reduce<
    {
      icon: PaymentIcon
      type: PaymentType
      subType: SubPaymentType | null
      basketOrder?: number
      __typename: 'AvailablePaymentOption'
    }[]
  >((accumulator, paymentOption) => {
    const paymentMethod = localPaymentMethodMap[paymentOption.option]

    if (!paymentMethod) {
      return accumulator
    }

    if (
      'icon' in paymentMethod &&
      Object.values(PaymentIcon).includes(paymentMethod.icon) &&
      Object.values(PaymentType).includes(paymentOption.option as PaymentType)
    ) {
      accumulator.push({
        icon: paymentMethod.icon,
        type: paymentOption.option as PaymentType,
        subType: null,
        basketOrder: paymentMethod.basketOrder,
        __typename: 'AvailablePaymentOption',
      })
    } else if (
      paymentMethod[paymentOption.subOption] &&
      Object.values(PaymentIcon).includes(
        paymentMethod[paymentOption.subOption].icon,
      ) &&
      Object.values(SubPaymentType).includes(
        paymentOption.subOption as SubPaymentType,
      )
    ) {
      const subPaymentMethod = paymentMethod[paymentOption.subOption]
      accumulator.push({
        icon: subPaymentMethod.icon,
        type: paymentOption.option as PaymentType,
        subType: paymentOption.subOption as SubPaymentType,
        basketOrder: subPaymentMethod.basketOrder,
        __typename: 'AvailablePaymentOption',
      })
    }

    return accumulator
  }, [])

  return formattedPaymentOptions.sort(
    (a, b) => (a.basketOrder || 0) - (b.basketOrder || 0),
  )
}

const transformQubitProduct = ({
  basketInfo,
  basketItems,
}: {
  basketItems: SharedBasketFragment['items']
  basketInfo: { total: { value: number; currency: string }; quantity: number }
}): QubitBasketItem[] => {
  return basketItems.map((item) => {
    const product = item.product
    return {
      basket: basketInfo,
      product: {
        productId: product.product?.sku.toString(),
        sku: product.sku.toString(),
        name: product.title,
        price: {
          value: parseFloat(item.standardPricePerUnit.amount),
          currency: item.standardPricePerUnit.currency,
        },
      },
      subtotal: {
        value: parseFloat(item.totalChargePrice.amount),
        currency: item.totalChargePrice.currency,
      },
      subtotalIncludingTax: {
        value: parseFloat(item.totalChargePrice.amount),
        currency: item.totalChargePrice.currency,
      },
      quantity: item.quantity,
    }
  })
}

const getCombinedProductVariantsFromSysProducts = (sysProduct) => {
  const productVariantsSkuSet = new Set()
  let combinedSysProductVariants = new Array()
  sysProduct.productVariants.forEach((productVariant) => {
    productVariantsSkuSet.add(productVariant.sku)
    combinedSysProductVariants.push(productVariant)
    if (!productVariant.images || !productVariant.images.length) {
      productVariant.images = productVariant.product.images || []
    }
  })
  sysProduct.products.forEach((product) => {
    combinedSysProductVariants = combinedSysProductVariants.concat(
      product.variants.filter((productVariant) => {
        if (productVariantsSkuSet.has(productVariant.sku)) {
          return false
        }
        productVariantsSkuSet.add(productVariant.sku)
        if (!productVariant.images || !productVariant.images.length) {
          productVariant.images = productVariant.product.images || []
        }
        return true
      }),
    )
  })
  return combinedSysProductVariants
}

export const normaliseBasket = (
  basket: SharedBasketFragment,
  productContentKeys?: DescriptionKeys,
  isQubitEnabled?: boolean,
): NormalisedBasket => {
  return {
    ...basket,
    qubitBasketItems: isQubitEnabled
      ? transformQubitProduct({
          basketItems: basket.items,
          basketInfo: {
            total: {
              value: parseFloat(basket.chargePrice.amount),
              currency: basket.chargePrice.currency,
            },
            quantity: basket.totalQuantity,
          },
        })
      : null,
    messages: basket.messages.filter((message) => {
      if (
        message.type === BasketMessageType.ItemsHaveErrors &&
        !basket.items.find(
          (item) =>
            item.messages &&
            item.messages.find(
              (itemMessage) =>
                itemMessage.type === BasketItemMessageType.NotEnoughStock ||
                itemMessage.type === BasketItemMessageType.ProductIssue ||
                itemMessage.type === BasketItemMessageType.FulfilmentIssue,
            ),
        )
      ) {
        return false
      }

      return true
    }),
    items: basket.items
      .map((item) => ({
        ...item,
        fulfilmentMethod: item.fulfilmentMethod || null,
        store: item.store || null,
        storeStock: item.storeStock || null,
        product: {
          ...item.product,
          externalIdentifier: item.product.product?.externalIdentifier || '',
          inWishlist: item.product.inWishlist || null,
          isSubscription: item.product.isSubscription,
          isAutoRenewSubscription: item.product.isAutoRenewSubscription,
          subscriptionFrequency: item.product.subscriptionFrequency,
          subscriptionTerm: item.product.subscriptionTerm,
          subscriptionPaymentType: item.product.subscriptionPaymentType,
          supersize: item.product.supersize,
          availableFrequencies:
            item.product?.subscriptionContracts?.length > 0 &&
            item.product.subscriptionContracts.map((contract) => ({
              key: contract.id,
              displayText: `${contract.frequencyDuration.duration} ${contract.frequencyDuration.unit}`,
              value: `${contract.frequencyDuration.duration} ${contract.frequencyDuration.unit}`,
            })),
        },
        messages: item.messages || null,
      }))
      .sort((a, b) => {
        return a.freeGift && !b.freeGift
          ? 1
          : !a.freeGift && b.freeGift
          ? -1
          : a.product.title < b.product.title
          ? -1
          : 1
      }),
    appliedOffers: basket.appliedOffers.map((appliedOffer) => ({
      ...appliedOffer,
      displayValue: appliedOffer.totalBasketDiscount?.displayValue || null,
      amount: appliedOffer.totalBasketDiscount?.amount || null,
    })),
    availablePaymentOptions: transformPaymentOptions(
      basket.availablePaymentOptions,
    ),
    selectYourSample: basket.selectYourSample.map((sample) => {
      const sysTiers = sample.tiers as SysTier[]
      return {
        ...sample,
        tiers: sysTiers.map((tier, index) => {
          const combinedSysProductVariants = getCombinedProductVariantsFromSysProducts(
            tier.sysProducts,
          )
          const inStockProducts = combinedSysProductVariants.filter(
            (product) => product.inStock,
          )
          const isFullyOutOfStock = inStockProducts.length === 0
          const isPartiallyOutOfStock =
            !isFullyOutOfStock &&
            inStockProducts.length < combinedSysProductVariants.length

          return {
            ...tier,
            isFullyOutOfStock,
            isPartiallyOutOfStock,
            products: filterAndSortSelectYourSampleProducts(
              combinedSysProductVariants,
              productContentKeys,
            ),
            id: `${sample.id}:${index}`,
            additionalSpendToReach: tier.additionalSpendToReach
              ? tier.additionalSpendToReach
              : {
                  amount: '0.00',
                  displayValue: '£0.00',
                  __typename: 'MoneyValue',
                },
            selectedProducts: tier.selectedSysProducts.productVariants,
          }
        }),
      }
    }),
    recommendations:
      basket.recommendations?.map((recommendation) => {
        return {
          ...recommendation,
          reviews: {
            total: recommendation.reviews?.averageScore ?? 0,
            averageScore: recommendation.reviews?.total ?? 0,
            __typename: 'ProductReviews',
          },
          image: recommendation.images[0]?.largeProduct ?? '',
          __typename: 'Product',
        }
      }) || [],
    earnableLoyaltyPoints: basket.earnableLoyaltyPoints || null,
    hasProductWithClickAndCollectFulfilment: basket.items.some(
      (item) =>
        item.fulfilmentMethod === FulfilmentMethod.DeliverToStore ||
        item.fulfilmentMethod === FulfilmentMethod.CollectInStore,
    ),
  }
}
